import { useState, useEffect, useCallback } from 'react';
import { favoriteService } from '../../services/favoriteService';
import { useCart } from '../../context/CartContext';
import { useNotifications } from '../../context/NotificationContext';
import { Order, OrderItem } from '../../types/order';

interface UseUserOrderActionsProps {
  onFavoriteChange?: (action: 'add' | 'remove', orderId: number, orderDetails?: Order) => void; // Favori değişikliği callback'i
}

export const useUserOrderActions = (props?: UseUserOrderActionsProps) => {
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const { addToCart } = useCart();
  const { showSuccess, showError } = useNotifications();

  // Kullanıcı bilgisi
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
  const userId = userInfo?.id;

  // Kullanıcının favori sipariş ID'lerini yükle
  const fetchFavoriteIds = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const favoriteIds = await favoriteService.getFavoriteIds(userId);
      setFavorites(new Set(favoriteIds));
    } catch (error) {
      console.error("Favori ID'leri yükleme hatası:", error);
    }
  }, [userId]);

  // Dışarıdan favori state'ini güncelleme fonksiyonu
  const updateFavoriteState = useCallback((orderId: number, isFavorite: boolean) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (isFavorite) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  }, []);

  // Sipariş detaylarını al
  const fetchOrderDetails = useCallback(async (orderId: number): Promise<Order | null> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${orderId}`);
      if (!response.ok) {
        throw new Error('Sipariş detayları alınamadı');
      }
      return await response.json();
    } catch (error) {
      console.error('Sipariş detayları alma hatası:', error);
      return null;
    }
  }, []);

  // Favori toggle
  const toggleFavorite = useCallback(
    async (orderId: number) => {
      if (!userId || loading) {
        return;
      } // Loading durumunda işlemi engelle

      setLoading(true); // Loading state'ini ayarla

      try {
        const isFavorite = favorites.has(orderId);

        if (isFavorite) {
          // Favorilerden kaldır
          await favoriteService.removeFromFavorites(userId, orderId);
          setFavorites(prev => {
            const newSet = new Set(prev);
            newSet.delete(orderId);
            return newSet;
          });
          showSuccess('Başarılı', 'Sipariş favorilerden kaldırıldı');

          // Favori kaldırma callback'ini çağır
          if (props?.onFavoriteChange) {
            props.onFavoriteChange('remove', orderId);
          }
        } else {
          // Favorilere ekle
          await favoriteService.addToFavorites(userId, orderId);
          setFavorites(prev => new Set([...prev, orderId]));
          showSuccess('Başarılı', 'Sipariş favorilere eklendi');

          // Sipariş detaylarını al ve callback'e gönder
          const orderDetails = await fetchOrderDetails(orderId);
          if (orderDetails && props?.onFavoriteChange) {
            props.onFavoriteChange('add', orderId, orderDetails);
          }
        }
      } catch (error) {
        console.error('Favori işlemi hatası:', error);
        showError('Hata', 'Favori işlemi gerçekleştirilemedi');
      } finally {
        setLoading(false); // Loading state'ini sıfırla
      }
    },
    [userId, favorites, loading, showSuccess, showError, props, fetchOrderDetails]
  );

  // Sipariş yenileme (reorder)
  const handleReorder = useCallback(
    (order: Order) => {
      if (!order.items || order.items.length === 0) {
        showError('Hata', 'Sipariş ürünleri bulunamadı');
        return;
      }

      order.items.forEach((item: OrderItem) => {
        addToCart(
          {
            id: item.product_id,
            name: item.name,
            price: item.price,
            options: {
              size: item.size as any,
              strength: item.strength as any,
            },
          },
          item.quantity
        );
      });
      showSuccess('Başarılı', 'Ürünler sepete eklendi');
    },
    [addToCart, showSuccess, showError]
  );

  // İlk yükleme - sadece userId değiştiğinde
  useEffect(() => {
    if (userId) {
      fetchFavoriteIds();
    }
  }, [userId, fetchFavoriteIds]); // fetchFavoriteIds'ı tekrar ekledik çünkü useCallback ile stable

  return {
    favorites,
    loading,
    toggleFavorite,
    handleReorder,
    updateFavoriteState,
  };
};
